import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Sparkles, ChevronRight, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/auth';
import { SparklesCore } from '../ui/sparkles';
import { motion, AnimatePresence } from 'framer-motion';

const BOOSTER_TYPES = [
  { id: 'water', name: 'Pack Aquatique', element: 'EAU', color: 'from-blue-400 to-cyan-500', icon: '🌊' },
  { id: 'fire', name: 'Pack Volcanique', element: 'FEU', color: 'from-red-400 to-orange-500', icon: '🔥' },
  { id: 'air', name: 'Pack Céleste', element: 'AIR', color: 'from-gray-400 to-slate-500', icon: '🌪️' }
];

export default function Booster() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedBoosters, setSelectedBoosters] = useState<string[]>([]);
  const [openingBooster, setOpeningBooster] = useState<string | null>(null);
  const [revealedCards, setRevealedCards] = useState<any[]>([]);
  const [hasCompletedStarterPacks, setHasCompletedStarterPacks] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    checkStarterPacksStatus();
  }, [user]);

  const checkStarterPacksStatus = async () => {
    if (!user) return;

    try {
      const { data: cards, error } = await supabase
        .from('cards')
        .select('id')
        .eq('owner_id', user.id);

      if (error) throw error;

      setHasCompletedStarterPacks(cards && cards.length > 0);
    } catch (err) {
      console.error('Error checking starter packs status:', err);
      setError('Erreur lors de la vérification du statut des packs de démarrage');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setRevealedCards([]);
  };

  const handleViewCollection = () => {
    handleCloseModal();
    navigate('/collection');
  };

  const handleBoosterSelect = async (boosterId: string) => {
    if (!user) {
      setError('Vous devez être connecté pour ouvrir des boosters');
      return;
    }

    if (selectedBoosters.length >= 2 || selectedBoosters.includes(boosterId)) return;

    setError(null);
    const newSelected = [...selectedBoosters, boosterId];
    setSelectedBoosters(newSelected);
    setOpeningBooster(boosterId);
    setShowModal(true);

    try {
      const booster = BOOSTER_TYPES.find(b => b.id === boosterId);
      
      // Use /.netlify/functions/generate-cards for production
      const response = await fetch('/.netlify/functions/generate-cards', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          element: booster?.element,
          count: 5,
          owner_id: user.id,
          isBoosterRequest: true
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error;
        } catch {
          errorMessage = errorText || 'Erreur lors de la génération des cartes';
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Erreur lors de la génération des cartes');
      }

      // Reveal cards one by one
      for (let i = 0; i < result.cards.length; i++) {
        await new Promise(resolve => setTimeout(resolve, 800));
        setRevealedCards(prev => [...prev, result.cards[i]]);
      }
    } catch (error) {
      console.error('Error generating cards:', error);
      setError(error instanceof Error ? error.message : 'Erreur lors de la génération des cartes');
      setShowModal(false);
    } finally {
      setOpeningBooster(null);
    }
  };

  if (hasCompletedStarterPacks) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Boosters de démarrage déjà réclamés</h2>
          <p className="mt-2 text-gray-600">Vous avez déjà ouvert vos boosters de démarrage.</p>
          <button
            onClick={() => navigate('/collection')}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
          >
            Voir ma collection
            <ChevronRight className="ml-2 h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Boosters de Démarrage</h1>
        <p className="mt-2 text-gray-600">
          Choisissez 2 boosters pour commencer votre collection !
          {selectedBoosters.length > 0 && ` (${selectedBoosters.length}/2)`}
        </p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
        {BOOSTER_TYPES.map((booster) => {
          const isSelected = selectedBoosters.includes(booster.id);
          const isDisabled = selectedBoosters.length === 2 && !isSelected;
          const isOpening = openingBooster === booster.id;

          return (
            <motion.div
              key={booster.id}
              whileHover={!isDisabled && !isSelected ? { scale: 1.05 } : {}}
              className={`relative overflow-hidden rounded-lg shadow-lg ${
                isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
              }`}
              onClick={() => !isDisabled && !isSelected && handleBoosterSelect(booster.id)}
            >
              <div className={`h-64 bg-gradient-to-br ${booster.color} p-6 relative`}>
                {isOpening && (
                  <SparklesCore
                    className="absolute inset-0 w-full h-full"
                    particleColor="#fff"
                  />
                )}
                
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <span className="text-6xl mb-4 block">{booster.icon}</span>
                    <h3 className="text-xl font-bold text-white mb-2">{booster.name}</h3>
                    {isSelected ? (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white text-gray-800">
                        <Sparkles className="h-4 w-4 mr-1" />
                        Sélectionné
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-black/20 text-white">
                        <Package className="h-4 w-4 mr-1" />
                        Choisir ce pack
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      <AnimatePresence>
        {showModal && revealedCards.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          >
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full relative">
              <button
                onClick={handleCloseModal}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Cartes obtenues ({revealedCards.length}/5)
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {revealedCards.map((card, index) => (
                  <motion.div
                    key={card.id}
                    initial={{ rotateY: 180, opacity: 0 }}
                    animate={{ rotateY: 0, opacity: 1 }}
                    transition={{ delay: index * 0.2 }}
                    className={`bg-gradient-to-br ${
                      card.rarete === 'GOLD' 
                        ? 'from-yellow-400 to-yellow-600'
                        : card.rarete === 'RARE'
                        ? 'from-blue-500 to-blue-700'
                        : 'from-gray-500 to-gray-700'
                    } rounded-lg p-4 text-white`}
                  >
                    <h4 className="font-bold">{card.surnom}</h4>
                    <p className="text-sm opacity-80">{card.rarete}</p>
                    <div className="mt-2 text-xs">
                      <p>ATT: {card.attaque}</p>
                      <p>DEF: {card.defense}</p>
                      <p>SPE: {card.special}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="mt-6 text-center">
                {selectedBoosters.length === 2 ? (
                  <button
                    onClick={handleViewCollection}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    Voir ma collection
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </button>
                ) : (
                  <button
                    onClick={handleCloseModal}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    Continuer
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}